import { Button, ChipGroup, Field, HStack, Text, VStack } from '@aj/ui';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { useT } from '../../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

export type ComposeProps = {
  /** Placeholder and accessible name — "Habit name", "Task name". */
  labelKey: MessageKey;
  /** What the submit button adds. The button reads "Add", but two composers
   *  can be open at once, and "Add" twice is ambiguous out loud. */
  actionKey: MessageKey;
  onSubmit: (name: string, plan?: HabitPlan) => void;
  onDismiss: () => void;
  /**
   * Offer the plan — the if-then sentence and the floor (§48 #1, #2).
   *
   * Appears only once the name has something in it, and never takes focus.
   * The fast path is untouched: type a name, press Enter, done. A student who
   * wants a plan sees the slots and fills them because they are the next
   * fields; a student in a hurry never knows they were there.
   */
  withFloor?: boolean;
};

/** What a habit gets beyond its name. All optional, all skippable. */
export type HabitPlan = { floor?: string; anchor?: string; place?: string };

/**
 * Anchors offered by default.
 *
 * Prayer times and school do the heavy lifting: they already happen at the
 * same point every day without anyone having to remember them, which is
 * exactly what an anchor must be. A clock is not an anchor — it can be missed
 * by forty minutes and then the plan is wrong (§53.1).
 */
const ANCHORS: MessageKey[] = [
  'anchor.fajr',
  'anchor.school',
  'anchor.asr',
  'anchor.maghrib',
  'anchor.dinner',
];

/**
 * Inline composer. One field, no sheet, no pickers.
 *
 * NOTHING BUT THE NAME. The floor and the if-then plan used to be offered
 * here and are not any more: a floor typed on day one is a solution to a
 * problem the student has not had, and a plan written before they have tried
 * once is a guess. Both are captured later by the coach, from a real
 * experience — the plan proposed from the hours the app has watched them
 * actually work, the floor asked the morning after a miss.
 *
 * `withFloor` remains for the habit detail screen, where nobody is in a hurry.
 *
 * The install→first-tick budget is under a minute and has to END with a tick,
 * so creating anything cannot cost more than typing a name. Everything else
 * about a habit or a task is editable later, from a screen nobody is in a
 * hurry on.
 *
 * Habits and tasks share it because the two flows are the same flow: type,
 * Enter, type the next one. Anywhere they diverge later is a prop, not a
 * second copy of this file.
 */
export function Compose({
  labelKey,
  actionKey,
  onSubmit,
  onDismiss,
  withFloor = false,
}: ComposeProps) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [floor, setFloor] = useState('');
  const [anchor, setAnchor] = useState('');
  const [place, setPlace] = useState('');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  const submit = () => {
    const name = draft.trim();
    if (!name) return;
    const plan: HabitPlan = {
      ...(floor.trim() ? { floor: floor.trim() } : {}),
      ...(anchor.trim() ? { anchor: anchor.trim() } : {}),
      ...(place.trim() ? { place: place.trim() } : {}),
    };
    setDraft('');
    setFloor('');
    setAnchor('');
    setPlace('');
    onSubmit(name, plan);
  };

  /** Enter submits from any slot; Escape leaves. Same contract everywhere, so
   *  the fast path works no matter which field the cursor is in. */
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submit();
    if (event.key === 'Escape') onDismiss();
  };

  const showExtras = withFloor && draft.trim().length > 0;

  const row = (
    <HStack gap={2}>
      <Field
        inputRef={field}
        value={draft}
        onChange={setDraft}
        onKeyDown={keys}
        onBlur={(event) => {
          // Moving to the floor field is not leaving the composer.
          if (!draft.trim() && !event.relatedTarget?.closest('[data-compose]')) onDismiss();
        }}
        label={t(labelKey)}
        className="flex-1"
      />
      <Button
        size="md"
        icon="plus"
        aria-label={t(actionKey)}
        onClick={submit}
        disabled={!draft.trim()}
      >
        {t('action.add')}
      </Button>
    </HStack>
  );

  if (!withFloor) return row;

  return (
    <VStack gap={3} data-compose>
      {row}

      {showExtras && (
        <VStack gap={3} className="pl-1">
          {/* A SENTENCE with slots, not a form with labels. The evidence is
              specifically for if-then plans, and writing the fields as a
              sentence is what keeps the format — three labelled boxes would
              collect the same words and lose the thing that makes them work. */}
          <VStack gap={2}>
            <HStack gap={2} align="center" className="flex-wrap">
              <Text variant="caption" tone="subtle">
                {t('plan.after')}
              </Text>
              <Field
                size="sm"
                value={anchor}
                onChange={setAnchor}
                onKeyDown={keys}
                label={t('plan.anchorName')}
                className="w-32"
              />
              <Text variant="caption" tone="subtle">
                {t('plan.at')}
              </Text>
              <Field
                size="sm"
                value={place}
                onChange={setPlace}
                onKeyDown={keys}
                label={t('plan.placeName')}
                className="w-32"
              />
            </HStack>

            {/* Anchors that already happen at the same point every day without
                anyone remembering them, which is the whole job of an anchor. */}
            <ChipGroup
              label={t('plan.anchorName')}
              options={ANCHORS.map((key) => ({ value: t(key), label: t(key) }))}
              value={anchor}
              onSelect={setAnchor}
              clearable
            />
          </VStack>

          <VStack gap={1}>
            <Field
              value={floor}
              onChange={setFloor}
              onKeyDown={keys}
              label={t('floor.label')}
              className="w-full"
            />
            <Text variant="caption" tone="subtle">
              {t('floor.hint')}
            </Text>
          </VStack>
        </VStack>
      )}
    </VStack>
  );
}
